/* ==========================================================================
   Luvre Fashion — Cart drawer upgrade
   Aangemaakt 31 juli 2026.

   Waarom custom elements en niet gewoon addEventListener:
   cart-drawer.js vervangt bij ELKE cart-wijziging de volledige innerHTML van
   #CartDrawer. Losse listeners zijn daarna weg. Custom elements worden door
   de browser automatisch opnieuw ge-upgrade zodra ze in de DOM verschijnen,
   dus connectedCallback draait vanzelf opnieuw. Dat maakt deze blokken
   bestand tegen elke re-render.
   ========================================================================== */

(function () {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SECTIONS = 'cart-drawer,cart-icon-bubble';

  /* ------------------------------------------------------------------------
     Gedeelde helpers
     ------------------------------------------------------------------------ */

  function postJSON(path, payload) {
    return fetch(ROOT.replace(/\/$/, '') + path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }).then(async (response) => {
      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }
      if (!response.ok) {
        const message = (data && (data.description || data.message)) || 'Something went wrong.';
        const error = new Error(message);
        error.data = data;
        throw error;
      }
      return data;
    });
  }

  /**
   * Haalt de cart-drawer en het cart-icoon opnieuw op bij de server en zet ze
   * terug in de DOM. Dit is dezelfde aanpak als Dawn zelf gebruikt, zodat alle
   * Liquid-logica (totalen, kortingsbadges, lege staat) door de server wordt
   * bepaald en niet in JS wordt nagebouwd.
   */
  async function renderCartSections() {
    const response = await fetch(`${ROOT}?sections=${SECTIONS}`);
    if (!response.ok) return;
    const sections = await response.json();
    const parser = new DOMParser();

    if (sections['cart-drawer']) {
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const freshInner = doc.querySelector('#CartDrawer');
      const targetInner = document.querySelector('#CartDrawer');
      const freshHost = doc.querySelector('cart-drawer');
      const targetHost = document.querySelector('cart-drawer');

      if (freshInner && targetInner) {
        targetInner.innerHTML = freshInner.innerHTML;
      }

      // is-empty moet meebewegen, anders blijft de lege staat hangen.
      if (freshHost && targetHost) {
        targetHost.classList.toggle('is-empty', freshHost.classList.contains('is-empty'));
      }

      // De overlay zit in de vervangen HTML, dus de sluit-listener is weg.
      const overlay = document.querySelector('#CartDrawer-Overlay');
      if (overlay && targetHost && typeof targetHost.close === 'function') {
        overlay.addEventListener('click', targetHost.close.bind(targetHost));
      }
    }

    if (sections['cart-icon-bubble']) {
      const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      const fresh = doc.querySelector('.shopify-section') || doc.body;
      const target = document.getElementById('cart-icon-bubble');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
    }

    // Laat de rest van het theme weten dat de cart is gewijzigd.
    document.dispatchEvent(new CustomEvent('luvre:cart-updated', { bubbles: true }));
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'luvre-cart-upgrade' });
      }
    } catch (e) {
      /* publish is optioneel, nooit de flow blokkeren */
    }
  }

  function setBusy(element, busy) {
    element.classList.toggle('is-busy', !!busy);
    element.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /* ------------------------------------------------------------------------
     1. Reserve / urgency balk

     Regels, bewust zo gekozen:
     - De vervaltijd staat in localStorage, dus de timer reset NIET bij een
       refresh of bij het navigeren naar een andere pagina. Een timer die
       terugspringt naar 10:00 verraadt zichzelf onmiddellijk.
     - Loopt hij af, dan wordt de cart NOOIT geleegd. De balk wisselt naar een
       rustige boodschap. Omzet weggooien om een timer geloofwaardig te maken
       is een slechte ruil.
     - Komt iemand later terug (na de respijtperiode), dan start een nieuwe
       periode. Anders zou de balk voor altijd op "verlopen" blijven staan.
     ------------------------------------------------------------------------ */

  const RESERVE_KEY = 'luvre:cart-reserve';
  // Fallback voor Safari private mode, waar localStorage kan gooien. Blijft
  // ook staan bij een re-render, want module-scope overleeft innerHTML.
  let reserveMemory = null;

  function readReserve() {
    try {
      const raw = window.localStorage.getItem(RESERVE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {
      /* valt terug op geheugen */
    }
    return reserveMemory;
  }

  function writeReserve(record) {
    reserveMemory = record;
    try {
      window.localStorage.setItem(RESERVE_KEY, JSON.stringify(record));
    } catch (e) {
      /* geheugen is al gezet */
    }
  }

  class LuvreReserveBar extends HTMLElement {
    connectedCallback() {
      this.minutes = parseInt(this.dataset.minutes, 10) || 10;
      // Hoe lang na afloop we de "verlopen" staat tonen voordat een nieuw
      // bezoek een nieuwe periode krijgt.
      this.graceMs = (parseInt(this.dataset.graceMinutes, 10) || 30) * 60 * 1000;
      this.timeEl = this.querySelector('[data-reserve-time]');

      const now = Date.now();
      let record = readReserve();

      if (!record || typeof record.expiresAt !== 'number' || now >= record.expiresAt + this.graceMs) {
        record = { expiresAt: now + this.minutes * 60 * 1000 };
        writeReserve(record);
      }

      this.expiresAt = record.expiresAt;
      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) window.clearInterval(this.interval);
    }

    tick() {
      const remaining = this.expiresAt - Date.now();

      if (remaining <= 0) {
        this.setAttribute('data-state', 'expired');
        if (this.interval) window.clearInterval(this.interval);
        return;
      }

      this.setAttribute('data-state', 'running');

      const totalSeconds = Math.floor(remaining / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const label = `${minutes}:${String(seconds).padStart(2, '0')}`;

      if (this.timeEl && this.timeEl.textContent !== label) {
        this.timeEl.textContent = label;
      }
    }
  }

  /* ------------------------------------------------------------------------
     2. Kortingscode

     Gebruikt de officiele discount-parameter van /cart/update.js (beschikbaar
     sinds mei 2025). Let op: die parameter VERVANGT de volledige set codes,
     dus bestaande codes moeten altijd meegestuurd worden.
     ------------------------------------------------------------------------ */

  class LuvreDiscountForm extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-discount-toggle]');
      this.panel = this.querySelector('[data-discount-panel]');
      this.input = this.querySelector('[data-discount-input]');
      this.submit = this.querySelector('[data-discount-submit]');
      this.message = this.querySelector('[data-discount-message]');

      this.applied = (this.dataset.appliedCodes || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);

      // Stond het paneel al open voor de re-render? Dan weer openen, anders
      // klapt het veld dicht terwijl iemand aan het typen was.
      if (this.dataset.keepOpen === 'true' || LuvreDiscountForm.wasOpen) {
        this.setOpen(true, false);
      }

      if (this.toggle) {
        this.toggle.addEventListener('click', () => {
          const open = this.panel.getAttribute('data-open') !== 'true';
          this.setOpen(open, open);
        });
      }

      if (this.submit) {
        this.submit.addEventListener('click', () => this.apply());
      }

      if (this.input) {
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.apply();
          }
        });
      }

      this.querySelectorAll('[data-discount-remove]').forEach((button) => {
        button.addEventListener('click', () => this.remove(button.dataset.discountRemove));
      });
    }

    setOpen(open, focus) {
      LuvreDiscountForm.wasOpen = open;
      if (this.panel) this.panel.setAttribute('data-open', open ? 'true' : 'false');
      if (this.toggle) this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && focus && this.input) this.input.focus();
    }

    say(text, tone) {
      if (!this.message) return;
      this.message.textContent = text || '';
      if (tone) {
        this.message.setAttribute('data-tone', tone);
      } else {
        this.message.removeAttribute('data-tone');
      }
    }

    async apply() {
      const code = (this.input && this.input.value.trim()) || '';
      if (!code) {
        this.say('Enter a discount code first.', 'error');
        return;
      }

      const alreadyApplied = this.applied.some((existing) => existing.toLowerCase() === code.toLowerCase());
      if (alreadyApplied) {
        this.say('That code is already applied.', 'error');
        return;
      }

      setBusy(this, true);
      this.say('');

      try {
        const next = this.applied.concat([code]);
        const cart = await postJSON('/cart/update.js', { discount: next.join(',') });
        const verdict = LuvreDiscountForm.verify(cart, code);

        if (verdict === false) {
          // Aantoonbaar niet geaccepteerd. Zet de oude set terug zodat er geen
          // ongeldige code blijft hangen, en zeg eerlijk wat er mis is.
          await postJSON('/cart/update.js', { discount: this.applied.join(',') });
          setBusy(this, false);
          this.say("That code isn't valid for the items in your cart.", 'error');
          return;
        }

        // verdict === null betekent: uit de response niet af te leiden. Dan
        // gewoon opnieuw renderen. De server-side chips tonen de waarheid.

        LuvreDiscountForm.wasOpen = true;
        await renderCartSections();
      } catch (error) {
        setBusy(this, false);
        this.say(error.message || 'We could not apply that code.', 'error');
      }
    }

    async remove(code) {
      setBusy(this, true);
      try {
        const next = this.applied.filter((existing) => existing.toLowerCase() !== String(code).toLowerCase());
        await postJSON('/cart/update.js', { discount: next.join(',') });
        LuvreDiscountForm.wasOpen = true;
        await renderCartSections();
      } catch (error) {
        setBusy(this, false);
        this.say(error.message || 'We could not remove that code.', 'error');
      }
    }
  }

  LuvreDiscountForm.wasOpen = false;

  /**
   * Bepaalt of een code daadwerkelijk is toegepast.
   * Geeft true (toegepast), false (aantoonbaar afgewezen) of null (niet af te
   * leiden uit de response). Bij null nooit een foutmelding tonen: dan zou een
   * geldige korting ten onrechte als ongeldig gemeld worden.
   */
  LuvreDiscountForm.verify = function (cart, code) {
    if (!cart) return null;
    const wanted = String(code).toLowerCase();

    if (Array.isArray(cart.discount_codes)) {
      const match = cart.discount_codes.find((entry) => entry && String(entry.code).toLowerCase() === wanted);
      if (match) return match.applicable === false ? false : true;
      return false;
    }

    if (Array.isArray(cart.cart_level_discount_applications)) {
      const match = cart.cart_level_discount_applications.find(
        (entry) => entry && String(entry.title).toLowerCase() === wanted
      );
      if (match) return true;
    }

    return null;
  };

  /* ------------------------------------------------------------------------
     3. Shipping protection

     Bewust default UIT. Een vooraf aangevinkte fee levert chargebacks en
     supporttickets op, en past niet bij een merk dat premium wil zijn.
     ------------------------------------------------------------------------ */

  class LuvreProtection extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-protect-input]');
      if (!this.input) return;

      this.variantId = this.dataset.variantId;
      this.lineKey = this.dataset.lineKey || '';

      this.input.addEventListener('change', () => this.onChange());
    }

    async onChange() {
      const wanted = this.input.checked;
      setBusy(this, true);

      try {
        if (wanted) {
          await postJSON('/cart/add.js', {
            items: [{ id: Number(this.variantId), quantity: 1 }],
          });
        } else if (this.lineKey) {
          await postJSON('/cart/change.js', { id: this.lineKey, quantity: 0 });
        }
        await renderCartSections();
      } catch (error) {
        // Terugdraaien zodat de toggle niet iets toont wat niet in de cart zit.
        this.input.checked = !wanted;
        setBusy(this, false);
      }
    }
  }

  /* ------------------------------------------------------------------------
     4. Upsell

     Producten hier hebben maten. Een "+ Add" die blind een maat toevoegt is
     een retour die staat te wachten, dus klapt hij een maatrij uit. Alleen
     producten met precies een variant worden direct toegevoegd.
     ------------------------------------------------------------------------ */

  class LuvreUpsell extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-upsell-add]').forEach((button) => {
        button.addEventListener('click', () => this.onAdd(button));
      });

      this.querySelectorAll('[data-upsell-size]').forEach((button) => {
        button.addEventListener('click', () => this.addVariant(button.dataset.upsellSize));
      });
    }

    onAdd(button) {
      const single = button.dataset.upsellAdd;

      // Precies een variant: direct toevoegen, geen onnodige klik.
      if (single) {
        this.addVariant(single);
        return;
      }

      const item = button.closest('[data-upsell-item]');
      const sizes = item && item.querySelector('[data-upsell-sizes]');
      if (!sizes) return;

      const open = sizes.getAttribute('data-open') !== 'true';

      // Andere kaarten dichtklappen, zodat er nooit twee rijen open staan.
      this.querySelectorAll('[data-upsell-sizes]').forEach((panel) => panel.setAttribute('data-open', 'false'));
      this.querySelectorAll('[data-upsell-add]').forEach((other) => other.setAttribute('aria-expanded', 'false'));

      sizes.setAttribute('data-open', open ? 'true' : 'false');
      button.setAttribute('aria-expanded', open ? 'true' : 'false');

      // Zonder dit klapt de maatrij open buiten beeld wanneer de upsell
      // onderaan het scrollgebied staat. De klant tikt dan op "+ Add" en ziet
      // niets gebeuren. `nearest` scrollt zo min mogelijk.
      if (open) {
        window.requestAnimationFrame(() => {
          try {
            sizes.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          } catch (e) {
            sizes.scrollIntoView(false);
          }
        });
      }
    }

    async addVariant(variantId) {
      if (!variantId) return;
      setBusy(this, true);

      try {
        await postJSON('/cart/add.js', {
          items: [{ id: Number(variantId), quantity: 1 }],
        });
        await renderCartSections();
      } catch (error) {
        setBusy(this, false);
        const errorEl = this.querySelector('[data-upsell-error]');
        if (errorEl) errorEl.textContent = error.message || 'We could not add that item.';
      }
    }
  }

  /* ------------------------------------------------------------------------
     Registratie
     ------------------------------------------------------------------------ */

  const registry = [
    ['lv-reserve-bar', LuvreReserveBar],
    ['lv-discount-form', LuvreDiscountForm],
    ['lv-protection', LuvreProtection],
    ['lv-upsell', LuvreUpsell],
  ];

  registry.forEach(([name, constructor]) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  });
})();
